import { useEffect, useState } from 'react'
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material'
import { Plus } from 'lucide-react'
import { tokens } from '../../theme'

/** A record as a picker shows it. */
export interface RecordOption {
  id: string
  label: string
  /** A second line: a code, a country, a count. */
  detail?: string
  /** Carried for the caller (e.g. an FO's code). */
  code?: string | null
}

type Choice = RecordOption | { create: string }

/**
 * Pick a record, or add one by name.
 *
 * Searches as you type (`onSearch` drives the caller's query) and offers
 * "Add “…”" when nothing matches exactly, so a person never has to leave
 * the screen to record a supplier, cooperative or farmer that is not on
 * file yet. Clearing the field unlinks.
 */
export function RecordPicker({
  value, options, loading, onSearch, onChange, onCreate, placeholder, ariaLabel, warn, disabled,
}: {
  value: RecordOption | null
  options: RecordOption[]
  loading?: boolean
  onSearch: (q: string) => void
  onChange: (option: RecordOption | null) => void
  /** Records a new one from the typed text and returns it. Omitted: no "Add". */
  onCreate?: (text: string) => Promise<RecordOption>
  placeholder?: string
  ariaLabel: string
  /** Shown in warning colour, as a missing required field is. */
  warn?: boolean
  disabled?: boolean
}) {
  const [input, setInput] = useState('')
  const [creating, setCreating] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => onSearch(input.trim()), 200)
    return () => clearTimeout(t)
    // onSearch is the caller's setter; the text is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input])

  const typed = input.trim()
  const exact = options.some((o) => o.label.toLowerCase() === typed.toLowerCase() || o.code?.toLowerCase() === typed.toLowerCase())
  const choices: Choice[] = [...options, ...(onCreate && typed && !exact ? [{ create: typed }] : [])]

  return (
    <Autocomplete<Choice, false, false, false>
      size="small"
      fullWidth
      disabled={disabled || creating}
      value={value}
      options={choices}
      loading={loading}
      filterOptions={(x) => x}
      isOptionEqualToValue={(a, b) => 'id' in a && 'id' in b && a.id === b.id}
      getOptionLabel={(o) => ('create' in o ? o.create : o.label)}
      onInputChange={(_, text, reason) => { if (reason === 'input') setInput(text) }}
      onChange={async (_, choice) => {
        if (!choice) return onChange(null)
        if ('create' in choice) {
          if (!onCreate) return
          setCreating(true)
          try {
            onChange(await onCreate(choice.create))
          } finally {
            setCreating(false)
          }
          return
        }
        onChange(choice)
      }}
      renderOption={(props, o) => (
        <Box component="li" {...props} key={'create' in o ? `create:${o.create}` : o.id}>
          {'create' in o ? (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', color: tokens.color.brandTeal }}>
              <Plus size={14} strokeWidth={1.5} /> Add “{o.create}”
            </Box>
          ) : (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2">{o.label}</Typography>
              {o.detail && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{o.detail}</Typography>}
            </Box>
          )}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          inputProps={{ ...params.inputProps, 'aria-label': ariaLabel }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(loading || creating) && <CircularProgress size={12} />}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: {
              fontSize: 13,
              '& fieldset': { borderColor: warn ? tokens.color.warning : 'transparent' },
              '&:hover fieldset': { borderColor: warn ? tokens.color.warning : tokens.color.ink300 },
            },
          }}
        />
      )}
    />
  )
}
